import React, { useState } from "react";
import { FiMapPin, FiTruck, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";

function CheckPincode() {
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // Expects: { success, serviceable, message, details }

  const handleCheck = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (pincode.length !== 6 || isNaN(pincode)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Pincode",
        text: "Please enter a valid 6-digit pincode.",
        confirmButtonColor: "#1a1a1a",
      });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      // API call to check hierarchical serviceability
      const res = await axios.get(`${baseurl}checkPincodeServiceability/${pincode}`);
      setStatus(res.data);
    } catch (err) {
      console.error("Check Pincode Error:", err);
      setStatus({
        success: false,
        serviceable: false,
        message: err.response?.data?.message || "Service currently unavailable for this location.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pincode-checker-wrapper mb-4">
      <div className="d-flex align-items-center mb-3">
        <FiTruck className="text-dark me-2" size={20} />
        <h6 className="fw-bold mb-0">Check Delivery Availability</h6>
      </div>

      <form onSubmit={handleCheck}>
        <div className="custom-pincode-group">
          {/* Icon Section */}
          <div className="pincode-icon-box">
            <FiMapPin size={18} />
          </div>

          {/* Input Field */}
          <input
            type="text"
            className="pincode-input-field"
            placeholder="Enter 6-digit Pincode"
            maxLength="6"
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
            required
          />

          {/* Action Button */}
          <button 
            type="submit" 
            className="pincode-check-btn" 
            disabled={loading || pincode.length !== 6}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              "CHECK"
            )}
          </button>
        </div>
      </form>

      {/* Result Display Logic */}
      {status && (
        <div className={`status-result-card mt-3 fade-in ${
          status.serviceable ? "status-success" : "status-error"
        }`}>
          <div className="me-2 mt-1">
            {status.serviceable ? (
              <FiCheckCircle className="text-success" size={18} />
            ) : (
              <FiAlertCircle className="text-danger" size={18} />
            )}
          </div>
          <div className="flex-grow-1">
            <p className="status-title mb-1">
              {status.serviceable ? "Serviceable" : "Not Serviceable"}
            </p>
            <p className="status-msg mb-0">{status.message}</p>
            
            {status.serviceable && status.details && (
              <div className="status-details mt-2 pt-2">
                <span className="location-badge">
                  {status.details.city}, {status.details.state}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .pincode-checker-wrapper {
          max-width: 450px;
        }

        /* Input Group Container */
        .custom-pincode-group {
          display: flex;
          align-items: stretch;
          background: #fff;
          border: 1px solid #ced4da;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s ease-in-out;
        }

        .custom-pincode-group:focus-within {
          border-color: #1a1a1a;
          box-shadow: 0 0 0 0.2rem rgba(26, 26, 26, 0.05);
        }

        /* Icon Box */
        .pincode-icon-box {
          background-color: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 15px;
          color: #6c757d;
          border-right: 1px solid #eee;
        }

        /* Input styling */
        .pincode-input-field {
          border: none !important;
          padding: 12px 15px;
          font-size: 1rem;
          font-weight: 500;
          color: #1a1a1a;
          outline: none !important;
          box-shadow: none !important;
          flex-grow: 1;
          width: 100%;
        }

        /* Button styling */
        .pincode-check-btn {
          background-color: #1a1a1a;
          color: #ffffff;
          border: none;
          padding: 0 25px;
          font-weight: 700;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
          transition: background 0.2s;
        }

        .pincode-check-btn:hover:not(:disabled) {
          background-color: #333;
        }

        .pincode-check-btn:disabled {
          background-color: #6c757d;
          cursor: not-allowed;
        }

        /* Status Cards */
        .status-result-card {
          display: flex;
          padding: 1rem;
          border-radius: 10px;
          border: 1px solid transparent;
        }

        .status-success {
          background-color: #f0fdf4;
          border-color: #bbf7d0;
        }

        .status-error {
          background-color: #fef2f2;
          border-color: #fecaca;
        }

        .status-title {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-success .status-title { color: #15803d; }
        .status-error .status-title { color: #b91c1c; }

        .status-msg {
          font-size: 0.9rem;
          color: #374151;
        }

        .status-details {
          border-top: 1px solid rgba(0,0,0,0.05);
        }

        .location-badge {
          background: #1a1a1a;
          color: #fff;
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 20px;
        }

        /* Animation */
        .fade-in {
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default CheckPincode;